// packages/web-ui/src/AppShell/drawer-tabs/DrawerTabStrip.tsx
//
// W2-S9 — internal tab strip for the UtilityDrawer (UX §7.3 + §7.4 line 1051).
//
// Renders three triggers — Activity / Detail / Debug — in a horizontal strip.
// Debug is conditionally rendered based on the union of:
//   - `tessa-lean.ui.showDebugTab` setting (default false), AND
//   - `debugOverride` session-only flag (Ctrl+Shift+J).
//
// Implementation note: rather than pulling shadcn/ui or `@base-ui/react`
// Tabs (the story doc allows the latter, but the dep is not yet wired),
// THIS strip is a lean ARIA-correct `<div role="tablist">` with three
// `<button role="tab">`s. The W2-S5 / W6-S1 follow-up may swap in Radix
// Tabs without changing the public component API.

import type { JSX } from 'react';
import type { DrawerTab } from '../../state/utilityDrawer.js';

export interface DrawerTabStripProps {
  /** Currently-active drawer tab. */
  activeTab: DrawerTab;
  /** Click handler — fires for every tab click (activity, detail, debug). */
  onTabChange: (tab: DrawerTab) => void;
  /** When `true`, render the Debug tab in addition to Activity + Detail. */
  showDebug: boolean;
}

interface TabSpec {
  id: DrawerTab;
  label: string;
}

const TAB_ORDER: ReadonlyArray<TabSpec> = [
  { id: 'activity', label: 'Activity' },
  { id: 'detail', label: 'Detail' },
  { id: 'debug', label: 'Debug' },
];

export function DrawerTabStrip({
  activeTab,
  onTabChange,
  showDebug,
}: DrawerTabStripProps): JSX.Element {
  const tabs = showDebug ? TAB_ORDER : TAB_ORDER.filter((t) => t.id !== 'debug');

  return (
    <div
      role="tablist"
      aria-label="Utility drawer tabs"
      data-testid="drawer-tab-strip"
      className="flex items-center gap-1 border-b border-soft bg-cream-100 px-2"
      style={{ height: 32 }}
    >
      {tabs.map((tab) => {
        const isActive = tab.id === activeTab;
        return (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={isActive}
            aria-controls={`drawer-panel-${tab.id}`}
            data-testid={`drawer-tab-${tab.id}`}
            data-state={isActive ? 'active' : 'inactive'}
            className={[
              'flex items-center gap-1.5 rounded-t px-3 py-1 text-xs font-medium',
              'text-taupe-700 hover:bg-cream-200',
              isActive
                ? 'border-b-2 border-taupe-700 bg-cream-300 text-taupe-900'
                : 'border-b-2 border-transparent',
            ].join(' ')}
            onClick={() => {
              onTabChange(tab.id);
            }}
          >
            {tab.label}
          </button>
        );
      })}
    </div>
  );
}
